import { FormArray, FormGroup } from '@angular/forms';
import { UserEffectivePermissionsService } from './user-effective-permissions.service';
import { RpeFieldRegistry, RpeFormScope } from 'src/app/shared/rpe/rpe.model';
import { RpeFieldPermissionHost } from 'src/app/shared/rpe/rpe-field-permission.host';
import { RpeSharedTabBinding } from 'src/app/shared/rpe/rpe-shared-tab.binding';
import { canApplyRpeFieldPermissions } from 'src/app/shared/rpe/rpe-module.helper';
import {
  normalizeFieldIdentity,
  registryEntryAppliesToFormScope,
} from 'src/app/shared/rpe/rpe-field.util';
import { SvgIconProp } from 'src/app/shared/models/dynamic.model';
import { ApplyRpeActionPermissionsOptions } from 'src/app/shared/rpe/rpe-action.util';
import { ModeIcons } from 'src/app/shared/utility/constants';

export function applyRpeFieldPermissionsToForm(
  service: UserEffectivePermissionsService,
  moduleNames: readonly string[],
  form: FormGroup,
  registry: RpeFieldRegistry,
  pageMode: string,
  formScope: RpeFormScope
): void {
  if (!canApplyRpeFieldPermissions(pageMode)) {
    return;
  }
  const moduleName = service.resolveModuleName(moduleNames);
  service.applyFieldPermissionsToForm(
    moduleName,
    form,
    registry,
    pageMode,
    formScope
  );
}

export function applyRpeFieldPermissionsToFormArray(
  service: UserEffectivePermissionsService,
  moduleNames: readonly string[],
  formArray: FormArray,
  pageMode: string,
  formScope: RpeFormScope,
  registry: RpeFieldRegistry
): void {
  if (!canApplyRpeFieldPermissions(pageMode)) {
    return;
  }
  formArray.controls.forEach((control) => {
    if (control instanceof FormGroup) {
      applyRpeFieldPermissionsToForm(
        service,
        moduleNames,
        control,
        registry,
        pageMode,
        formScope
      );
    }
  });
}

export function applyRpeFieldPermissionsToGridForm(
  service: UserEffectivePermissionsService,
  moduleNames: readonly string[],
  gridForm: FormGroup,
  pageMode: string,
  formScope: RpeFormScope,
  registry: RpeFieldRegistry
): void {
  const rows = gridForm.get('rows');
  if (rows instanceof FormArray) {
    applyRpeFieldPermissionsToFormArray(
      service,
      moduleNames,
      rows,
      pageMode,
      formScope,
      registry
    );
  }
}

export function isRpeRegistryFieldPermitted(
  host: RpeFieldPermissionHost,
  moduleNames: readonly string[],
  identityName: string,
  pageMode: string,
  formScope: RpeFormScope,
  registry: RpeFieldRegistry
): boolean {
  if (!canApplyRpeFieldPermissions(pageMode)) {
    return true;
  }
  const moduleName = host.resolveModuleName(moduleNames);
  const entry = registry.find(
    (candidate) =>
      candidate.binding.identityName === identityName &&
      registryEntryAppliesToFormScope(candidate, formScope)
  );
  if (!entry) {
    return !host.isFieldEnforcementActive(moduleName, pageMode);
  }
  return !host.isFieldDisabled(moduleName, entry, pageMode);
}

function hasModuleFieldPermissionByFieldName(
  service: UserEffectivePermissionsService,
  moduleNames: readonly string[],
  fieldName: string
): boolean {
  const moduleName = service.resolveModuleName(moduleNames);
  const module = service.getModuleByName(moduleName);
  if (!module?.fieldPermissions?.length) {
    return false;
  }
  const normalizedFieldName = normalizeFieldIdentity(fieldName);
  return module.fieldPermissions.some(
    (field) => normalizeFieldIdentity(field.fieldName) === normalizedFieldName
  );
}

/** Routes tab actions — edit mode only when RPE is on; matched by API fieldName. */
export function isRpeRouteActionPermitted(
  service: UserEffectivePermissionsService,
  moduleNames: readonly string[],
  fieldName: string,
  pageMode: string
): boolean {
  if (!service.isEnforcementEnabled() || service.isLoggedInSuperAdmin()) {
    return true;
  }
  if (pageMode !== ModeIcons.edit) {
    return false;
  }
  const moduleName = service.resolveModuleName(moduleNames);
  if (!service.isFieldEnforcementActive(moduleName, pageMode)) {
    return true;
  }
  return hasModuleFieldPermissionByFieldName(service, moduleNames, fieldName);
}

export function applyRpeModuleActionPermissionsToIcons(
  service: UserEffectivePermissionsService,
  moduleNames: readonly string[],
  icons: SvgIconProp[],
  options?: ApplyRpeActionPermissionsOptions
): SvgIconProp[] {
  const moduleName = service.resolveModuleName(moduleNames);
  return service.applyActionPermissionsToIcons(moduleName, icons, options);
}

export function createRpeSharedTabBinding(
  service: UserEffectivePermissionsService,
  config: {
    pageMode: string;
    formScope: RpeFormScope;
    moduleNames: readonly string[];
    registry: RpeFieldRegistry;
  }
): RpeSharedTabBinding {
  const { pageMode, formScope, moduleNames, registry } = config;
  return {
    pageMode,
    formScope,
    moduleNames,
    registry,
    applyToForm: (form) =>
      applyRpeFieldPermissionsToForm(
        service,
        moduleNames,
        form,
        registry,
        pageMode,
        formScope
      ),
    isIdentityPermitted: (identityName) =>
      isRpeRegistryFieldPermitted(
        service,
        moduleNames,
        identityName,
        pageMode,
        formScope,
        registry
      ),
  };
}

export function createRpeGridTabBinding(
  service: UserEffectivePermissionsService,
  config: {
    pageMode: string;
    formScope: RpeFormScope;
    moduleNames: readonly string[];
    registry: RpeFieldRegistry;
  }
): RpeSharedTabBinding {
  const { pageMode, formScope, moduleNames, registry } = config;
  return {
    pageMode,
    formScope,
    moduleNames,
    registry,
    applyToForm: (form) =>
      applyRpeFieldPermissionsToGridForm(
        service,
        moduleNames,
        form,
        pageMode,
        formScope,
        registry
      ),
  };
}
